import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '@/api/apiClient';
import { useOdooAuth } from '@/lib/OdooAuthContext';

/**
 * Single source of truth for the current user's SellGo profile.
 *
 * The profile (see `sellgo.rep.profile` on the Odoo side) tells us:
 *   • spending caps (order_confirm_limit)
 *   • which payment methods this user is allowed to log
 *   • which screens to hide from them
 *   • whether they can add customers/products, edit prices, etc.
 *
 * The whole app reads from this context instead of duplicating
 * permission checks. A missing/failed fetch falls back to a permissive
 * default so the app still works on installs without the addon.
 */

const DEFAULT_PROFILE = {
  active: true,
  order_confirm_limit: 0,             // 0 = no limit
  require_manager_approval_above_limit: false,
  allow_cash: true,
  allow_check: true,
  allow_bank_transfer: true,
  allow_wallet: true,
  allow_advance_payment: true,
  default_journal_id: false,
  warehouse_ids: [],
  dealer_category_ids: [],
  allow_add_customer: true,
  allow_add_product: true,
  allow_edit_price: true,
  allow_create_order: true,
  allow_confirm_order: true,
  allow_cancel_order: true,
  allow_log_visit: true,
  hidden_screens: [],
  supervisor_id: false,
  notes: '',
};

const RepProfileContext = createContext({
  profile: DEFAULT_PROFILE,
  loading: false,
  refresh: async () => {},
  // Helper: is this screen visible for the current user?
  isScreenVisible: () => true,
  // Helper: is a given payment method allowed?
  isMethodAllowed: () => true,
});

export const RepProfileProvider = ({ children }) => {
  const { odooSession } = useOdooAuth();
  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [loading, setLoading] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!odooSession?.uid) {
      setProfile(DEFAULT_PROFILE);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get('/sellgo_my_profile');
      if (res?.success && res.data) {
        // The server returns null for users without a profile yet (the
        // addon should auto-create one on first call, but be defensive).
        setProfile({ ...DEFAULT_PROFILE, ...res.data });
      } else {
        setProfile(DEFAULT_PROFILE);
      }
    } catch {
      // Network/server error — keep the permissive defaults so the
      // user isn't accidentally locked out by an addon hiccup.
      setProfile(DEFAULT_PROFILE);
    } finally {
      setLoading(false);
    }
  }, [odooSession?.uid]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  /**
   * For managers the profile flags are irrelevant — they always see
   * everything. We expose a helper so screens don't have to repeat
   * the "if manager then bypass" check.
   */
  const isManager = odooSession?.role === 'manager';

  const isScreenVisible = (screenKey) => {
    if (isManager) return true;
    const hidden = profile.hidden_screens || [];
    return !hidden.includes(screenKey);
  };

  const isMethodAllowed = (methodKey) => {
    if (isManager) return true;
    const map = {
      cash: profile.allow_cash,
      check: profile.allow_check,
      bank: profile.allow_bank_transfer,
      wallet: profile.allow_wallet,
    };
    // Default to true for unknown methods so existing UI doesn't break.
    return methodKey in map ? !!map[methodKey] : true;
  };

  const value = {
    profile,
    loading,
    isManager,
    refresh: fetchProfile,
    isScreenVisible,
    isMethodAllowed,
  };

  return (
    <RepProfileContext.Provider value={value}>
      {children}
    </RepProfileContext.Provider>
  );
};

export const useRepProfile = () => {
  const ctx = useContext(RepProfileContext);
  if (!ctx) {
    throw new Error('useRepProfile must be used inside <RepProfileProvider>');
  }
  return ctx;
};
